/**
 * Shared visual vocabulary for airspace-volume polygons.
 *
 * Two places draw operational volumes and they must not diverge:
 *   - `lib/map/layers/oi.ts`         — OI polygons on the main map, many at
 *                                      once, styled with Mapbox data-driven
 *                                      expressions off each feature's status.
 *   - `features/flights/FlightMap.tsx` — one flight's volume on its own map,
 *                                      styled with literal values.
 *
 * The two consumers need different *mechanisms* (expression vs literal), so
 * this module deliberately exports raw tokens rather than finished paint
 * objects. What it guarantees is that "amber dashed" means the same thing in
 * both places, and that a colour cannot be redefined in one without the other.
 *
 * Grammar:
 *   colour  — severity / state (green ok · blue approved · amber provisional ·
 *             red non-conforming or aborted · gray unknown or historical)
 *   dash    — confidence / liveness (solid = current and trusted; the looser
 *             the dash, the less the polygon asserts about right now)
 */

export const VOLUME_PALETTE = {
  green: '#22a050',
  blue: '#2563eb',
  amber: '#d97706',
  red: '#c41a1a',
  gray: '#6b7280',
  /** Cross-tenant partner volume — mirrors `--color-partner`. */
  partner: '#9aa3af',
  /** Unaffiliated DSS-discovered volume — mirrors `--color-purple`. */
  thirdparty: '#7c3aed',
} as const;

/** Mapbox `line-dasharray` patterns. `[1]` is dash-1/gap-0, i.e. solid. */
export const VOLUME_DASH = {
  solid: [1],
  /** Least assertive — unsubmitted, stale, unknown. */
  loose: [8, 5],
  /** Provisional — contingent, planned. */
  medium: [6, 3],
  /** Alarming — non-conforming, aborted. */
  tight: [4, 3],
  /** Partner-owned volume — mockup Leaflet `dashArray:'5 4'` at weight 2
   *  (Mapbox dashes are in line-width units, so halved). */
  partner: [2.5, 2],
  /** 3rd-party DSS volume — mockup `dashArray:'2 5'`, halved likewise. */
  thirdparty: [1, 2.5],
} as const;

export const VOLUME_LINE_WIDTH = 2;
export const VOLUME_LINE_WIDTH_FOCUSED = 4;
